import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobe, FaPaperPlane, FaMailBulk, FaRocket, FaBrain, FaChartBar, FaBriefcase, FaBullseye, FaCheckCircle, FaClock } from 'react-icons/fa';
import './Contact.css';

const Contact = ({ selectedServices = [] }) => {
  // Determine content based on selected services
  const isDigitalMarketing = selectedServices.includes('digital-marketing');
  const isWebDevelopment = selectedServices.includes('web-development');
  const isBothServices = isDigitalMarketing && isWebDevelopment;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error', or null

  // Dynamic content configuration
  const getContactContent = () => {
    if (isBothServices) {
      return {
        title: "Let's Build Your Complete Digital Empire",
        introTitle: "Ready for a Full-Stack Digital Transformation?",
        introQuestions: [
          { icon: <FaRocket />, text: "Need a website that actually converts AND the marketing to drive traffic to it?" },
          { icon: <FaBrain />, text: "Want a developer who thinks like a marketer (and vice versa)?" },
          { icon: <FaChartBar />, text: "Ready for an integrated approach that maximizes your ROI?" }
        ],
        introCta: "Let's build your complete digital empire - from code to campaigns.",
        helpItems: [
          "✅ Custom Web Development + Marketing Integration",
          "✅ E-commerce Platforms + Performance Marketing",
          "✅ Full-Stack Applications + Growth Automation",
          "✅ Brand Building + Technical Implementation",
          "✅ Analytics Setup + Conversion Optimization"
        ],
        ctaTitle: "Let's Engineer Your Success",
        ctaText: "Ready to build a digital ecosystem that grows your business 24/7? From high-converting websites to viral marketing campaigns, let's create your complete growth machine.",
        primaryCta: "Start My Digital Transformation",
        secondaryCta: "See My Complete Work"
      };
    } else if (isDigitalMarketing) {
      return {
        title: "Let's Grow Your Brand & Boost Your ROI",
        introTitle: "Ready to Build Your Marketing Empire?",
        introQuestions: [
          { icon: <FaRocket />, text: "Want marketing campaigns that actually drive sales (not just vanity metrics)?" },
          { icon: <FaBrain />, text: "Need a strategist who understands both creativity and data?" },
          { icon: <FaChartBar />, text: "Ready to scale your brand with proven, profitable systems?" }
        ],
        introCta: "Let's build marketing campaigns that turn followers into paying customers.",
        helpItems: [
          "✅ Social Media Growth & Viral Content Strategy",
          "✅ Meta Ads, Google Ads & Performance Campaigns",
          "✅ Brand Building & Authority Positioning",
          "✅ Marketing Automation & Lead Funnels",
          "✅ SEO & Content Marketing Systems"
        ],
        ctaTitle: "Let's Scale Your Brand",
        ctaText: "Ready to transform your marketing from expense to profit center? Let's create campaigns that don't just get attention - they drive results and grow your business.",
        primaryCta: "Grow My Brand Now",
        secondaryCta: "View Marketing Results"
      };
    } else if (isWebDevelopment) {
      return {
        title: "Let's Build Your Digital Foundation",
        introTitle: "Ready for a Website That Actually Works?",
        introQuestions: [
          { icon: <FaRocket />, text: "Want a website that converts visitors into customers (not just looks pretty)?" },
          { icon: <FaBrain />, text: "Need a developer who understands business goals, not just code?" },
          { icon: <FaChartBar />, text: "Ready for a platform that's built for performance and growth?" }
        ],
        introCta: "Let's build a website that works as hard as you do.",
        helpItems: [
          "✅ Custom Web Applications & E-commerce Platforms",
          "✅ WordPress Solutions & Performance Optimization",
          "✅ React Development & Modern Frontend",
          "✅ Full-Stack Solutions & Database Design",
          "✅ Mobile-First Design & SEO Integration"
        ],
        ctaTitle: "Let's Build Your Platform",
        ctaText: "Ready to turn your website from a digital business card into a growth engine? Let's create a platform that attracts, converts, and scales with your business.",
        primaryCta: "Build My Website",
        secondaryCta: "See Development Work"
      };
    }
    // Fallback to original content
    return {
      title: "Let's Work Together",
      introTitle: "Ready to Build Your Growth Machine?",
      introQuestions: [
        { icon: <FaRocket />, text: "Want a brand that actually works, not just looks pretty?" },
        { icon: <FaBrain />, text: "Need a developer who thinks like a marketer?" },
        { icon: <FaChartBar />, text: "Or a marketer who codes?" }
      ],
      introCta: "Let's build your growth machine.",
      helpItems: [
        "✅ Brand Building & Growth Strategy",
        "✅ Social Media Marketing & Campaigns",
        "✅ Marketing Automation & Funnel Creation",
        "✅ Full-Stack Web Development",
        "✅ SEO & Performance Analytics"
      ],
      ctaTitle: "Let's Build Your Future",
      ctaText: "Ready to turn your vision into a high-performing reality? Whether it's a powerful brand, a scalable marketing campaign, or a seamless web experience, let's connect.",
      primaryCta: "Discuss Your Project",
      secondaryCta: "Explore My Work"
    };
  };

  // Dynamic service options based on selection
  const getServiceOptions = () => {
    if (isBothServices) {
      return [
        { value: "full-stack-solution", label: "Complete Digital Solution (Web + Marketing)" },
        { value: "web-development", label: "Custom Web Development" },
        { value: "ecommerce-marketing", label: "E-commerce + Marketing Integration" },
        { value: "social-media-marketing", label: "Social Media Marketing & Growth" },
        { value: "performance-marketing", label: "Performance Marketing & Ads" },
        { value: "other", label: "Other / Multiple Services" }
      ];
    } else if (isDigitalMarketing) {
      return [
        { value: "social-media-marketing", label: "Social Media Marketing & Growth" },
        { value: "performance-marketing", label: "Meta Ads & Google Ads Management" },
        { value: "brand-building", label: "Brand Building & Content Strategy" },
        { value: "marketing-automation", label: "Marketing Automation & Funnels" },
        { value: "seo-content", label: "SEO & Content Marketing" },
        { value: "other", label: "Other Marketing Services" }
      ];
    } else if (isWebDevelopment) {
      return [
        { value: "custom-web-app", label: "Custom Web Application" },
        { value: "ecommerce-development", label: "E-commerce Platform (Shopify/Custom)" },
        { value: "wordpress-development", label: "WordPress Website & Customization" },
        { value: "web-redesign", label: "Website Redesign & Optimization" },
        { value: "full-stack-development", label: "Full-Stack Development" },
        { value: "other", label: "Other Development Services" }
      ];
    }
    // Fallback to original options
    return [
      { value: "web-development", label: "Web Development" },
      { value: "social-media-marketing", label: "Social Media Marketing" },
      { value: "performance-marketing", label: "Performance Marketing" },
      { value: "creative-design", label: "Creative Design" },
      { value: "ui-ux-design", label: "UI/UX Design" }
    ];
  };

  const content = getContactContent();
  const serviceOptions = getServiceOptions();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus(null);

    try {
      // Determine API endpoint based on environment
      const isDevelopment = window.location.hostname === 'localhost';
      const apiEndpoint = isDevelopment 
        ? 'http://localhost:8080/api/contact.php'
        : '/api/contact.php';
      
      console.log('Submitting to:', apiEndpoint);
      
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmissionStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        throw new Error('Failed to send message.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      // Check if we're in development mode
      const isDevelopment = window.location.hostname === 'localhost';
      
      if (isDevelopment) {
        // In development, show a helpful message
        alert(`Development Mode: Contact form data would be sent in production.\n\nForm Data:\nName: ${formData.name}\nEmail: ${formData.email}\nService: ${formData.service}\nMessage: ${formData.message}\n\nTo test fully, deploy to production or start PHP server.`);
        
        // Reset form and show success for demo purposes
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
        setSubmissionStatus('success');
      } else {
        setSubmissionStatus('error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "Email",
      info: "imdigitalharish@gmail.com",
      link: "mailto:imdigitalharish@gmail.com"
    },
    {
      icon: <FaPhone />,
      title: "Phone",
      info: "+91 96020 03790",
      link: "tel:+919602003790"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Location",
      info: "Ahmedabad, India",
      link: null
    },
    {
      icon: <FaGlobe />,
      title: "Website",
      info: "www.portfolioweb.fliphatmedia.com",
      link: "https://www.portfolioweb.fliphatmedia.com"
    }
  ];

  // SEO content based on selected services
  const getSEOContent = () => {
    if (isBothServices) {
      return {
        title: "Contact Harish Kumar - Full-Stack Digital Solutions & Marketing Expert",
        description: "Ready for complete digital transformation? Contact Harish Kumar for integrated web development and marketing solutions. Free consultation for full-stack projects.",
        keywords: "contact digital marketing expert, hire full-stack developer, web development consultation, marketing services ahmedabad, project quote"
      };
    } else if (isDigitalMarketing) {
      return {
        title: "Contact Harish Kumar - Digital Marketing Expert for Brand Growth",
        description: "Ready to grow your brand? Contact Harish Kumar for proven digital marketing strategies. Meta Ads, Google Ads, and social media marketing consultation available.",
        keywords: "contact digital marketing expert, Meta ads consultant, Google ads specialist, social media marketing ahmedabad, brand growth consultation"
      };
    } else if (isWebDevelopment) {
      return {
        title: "Contact Harish Kumar - Full-Stack Web Developer for Custom Solutions",
        description: "Need a custom website or web application? Contact Harish Kumar for full-stack development services. React, PHP, WordPress, and e-commerce solutions.",
        keywords: "contact web developer, hire React developer, custom web development, WordPress developer ahmedabad, full-stack consultation"
      };
    }
    return {
      title: "Contact Harish Kumar - Digital Marketing & Web Development Services",
      description: "Get in touch with Harish Kumar for digital marketing and web development services. Free consultation available for all projects.",
      keywords: "contact harish kumar, digital marketing consultation, web development services, ahmedabad developer"
    };
  };

  const seoContent = getSEOContent();

  return (
    <>
      <Helmet>
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <meta name="keywords" content={seoContent.keywords} />
        <meta property="og:title" content={seoContent.title} />
        <meta property="og:description" content={seoContent.description} />
        <meta property="og:url" content="https://portfolioweb.fliphatmedia.com/#contact" />
        <meta name="twitter:title" content={seoContent.title} />
        <meta name="twitter:description" content={seoContent.description} />
        <link rel="canonical" href="https://portfolioweb.fliphatmedia.com/#contact" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "mainEntity": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "imdigitalharish@gmail.com",
              "telephone": "+919602003790",
              "areaServed": "IN",
              "availableLanguage": ["English", "Hindi", "Gujarati"],
              "hoursAvailable": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                "opens": "09:00",
                "closes": "18:00"
              }
            }
          })}
        </script>
      </Helmet>
      
      <section id="contact" className="contact-section section">
      <div className="container">
        <h2 className="section-title"><FaMailBulk className="section-icon" /> {content.title}</h2>
        
        <div className="contact-intro">
          <h3>{content.introTitle}</h3>
          <div className="intro-questions">
            {content.introQuestions.map((item, index) => (
              <p key={index}>{item.icon} {item.text}</p>
            ))}
          </div>
          <p className="intro-cta">{content.introCta}</p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h3 className="content-title">Get In Touch</h3>
            <div className="contact-cards">
              {contactInfo.map((item, index) => (
                <div key={index} className="contact-card">
                  <div className="contact-icon">
                    {item.icon}
                  </div>
                  <div className="contact-details">
                    <h4>{item.title}</h4>
                    {item.link ? (
                      <a href={item.link} target="_blank" rel="noopener noreferrer">
                        {item.info}
                      </a>
                    ) : (
                      <span>{item.info}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="availability">
              <div className="availability-card">
                <h4><FaBriefcase className="list-icon" /> How I Can Help:</h4>
                <ul>
                  {content.helpItems.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <h3 className="content-title">Send Me a Message</h3>
            <form className="contact-form" onSubmit={handleSubmit}>
              
              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="Your Phone (Optional)"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="input-wrapper select-wrapper">
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>Select a service</option>
                    {serviceOptions.map((option, index) => (
                      <option key={index} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div className="input-wrapper">
                  <textarea
                    name="message"
                    placeholder="Tell me about your project or how I can help..."
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>Sending... <FaPaperPlane /></>
                ) : (
                  <>Send Message <FaPaperPlane /></>
                )}
              </button>

              {submissionStatus === 'success' && (
                <div className="submission-success">
                  <div className="success-icon">
                    <FaCheckCircle />
                  </div>
                  <div className="success-content">
                    <h3>Thank You for Reaching Out!</h3>
                    <p>Your message has been successfully delivered to my inbox. Here's what happens next:</p>
                    <ul className="success-steps">
                      <li><FaClock /> <strong>Response Time:</strong> I'll personally review your message and respond within 24 hours</li>
                      <li><FaEnvelope /> <strong>Confirmation:</strong> You should receive a confirmation email shortly</li>
                      <li><FaPhone /> <strong>Follow-up:</strong> For urgent matters, feel free to call me directly</li>
                    </ul>
                    <p className="success-note">
                      I'm excited to learn more about your project and explore how we can work together to achieve your goals!
                    </p>
                  </div>
                </div>
              )}
              {submissionStatus === 'error' && (
                <p className="submission-status error">Something went wrong. Please try again or email me directly.</p>
              )}
            </form>
          </div>
        </div>

        <div className="cta-section">
          <div className="cta-card">
            <h3><FaBullseye className="list-icon" /> {content.ctaTitle}</h3>
            <p>
              {content.ctaText}
            </p>
            <div className="cta-buttons">
              <a href="mailto:imdigitalharish@gmail.com" className="btn btn-primary">
                {content.primaryCta}
              </a>
              <a href="https://www.portfolioweb.fliphatmedia.com" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                {content.secondaryCta}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default Contact;
